import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Play,
  BarChart3,
  MessageSquare,
  MessageCircle,
  Activity,
  Key,
  ChevronLeft,
  ChevronRight,
  Mail,
  X,
  Clock,
  Newspaper,
  ArrowUpRight,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useContactMessagesUnreadCount } from "@/hooks/useAdminApi";

const navGroups = [
  {
    label: "Workspace",
    items: [
      { path: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
      { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { path: "/admin/activity", label: "Activity log", icon: Activity },
    ],
  },
  {
    label: "Community",
    items: [
      { path: "/admin/users", label: "People", icon: Users },
      { path: "/admin/replays", label: "Replays", icon: Play },
      { path: "/admin/comments", label: "Comments", icon: MessageCircle },
      { path: "/admin/announcements", label: "Announcements", icon: Newspaper },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        path: "/admin/contact-messages",
        label: "Inbox",
        icon: Mail,
        showBadge: true,
      },
      { path: "/admin/feedbacks", label: "Feedback", icon: MessageSquare },
      { path: "/admin/jobs", label: "Processing & jobs", icon: Clock },
      { path: "/admin/tokens", label: "Access tokens", icon: Key },
    ],
  },
];
interface AdminSidebarProps {
  className?: string;
  onClose?: () => void;
  isMobile?: boolean;
}

export function AdminSidebar({
  className,
  onClose,
  isMobile,
}: AdminSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { count: unreadContactCount } = useContactMessagesUnreadCount();
  const compact = collapsed && !isMobile;
  return (
    <aside
      className={cn(
        "bc-admin-sidebar",
        compact ? "w-[76px]" : "w-[244px]",
        className,
      )}
      aria-label="Administration"
    >
      <div className="bc-admin-sidebar-head">
        {!compact && <span>Control room</span>}
        <button
          onClick={isMobile ? onClose : () => setCollapsed(!collapsed)}
          aria-label={
            isMobile
              ? "Close navigation"
              : compact
                ? "Expand navigation"
                : "Collapse navigation"
          }
        >
          {isMobile ? (
            <X size={17} />
          ) : compact ? (
            <ChevronRight size={17} />
          ) : (
            <ChevronLeft size={17} />
          )}
        </button>
      </div>
      <nav className="bc-admin-nav" aria-label="Admin pages">
        {navGroups.map((group) => (
          <div key={group.label} className="bc-admin-nav-group">
            {!compact && <p>{group.label}</p>}
            {group.items.map(({ path, label, icon: Icon, ...options }) => (
              <NavLink
                key={path}
                to={path}
                end={"end" in options && options.end}
                onClick={isMobile ? onClose : undefined}
                title={compact ? label : undefined}
                className={compact ? "justify-center" : undefined}
              >
                <Icon />
                {!compact && <span>{label}</span>}
                {"showBadge" in options &&
                  options.showBadge &&
                  unreadContactCount > 0 && (
                    <small aria-label={`${unreadContactCount} unread messages`}>
                      {unreadContactCount > 99 ? "99+" : unreadContactCount}
                    </small>
                  )}
              </NavLink>
            ))}
          </div>
        ))}
        <NavLink
          to="/"
          title={compact ? "Back to BallCam" : undefined}
          onClick={isMobile ? onClose : undefined}
        >
          <ArrowUpRight />
          {!compact && <span>Back to BallCam</span>}
        </NavLink>
      </nav>
      {!compact && (
        <div className="bc-admin-sidebar-foot">BallCam · Administration</div>
      )}
    </aside>
  );
}
export default AdminSidebar;
